import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {chatCacheScope, migrateLegacyOutbox} from '../cache/chatCache';
import {readOutboxMessages, removeOutboxMessage, saveOutboxMessage} from '../cache/chatOutbox';
import {postingBlockedMessage} from '../serverMessages';

const emptyMessages = [];
const storageWarning = 'Unsent content could not be saved. It is kept on this page, but may not survive a refresh.';

export default function useMessageOutbox({conversation, currentUser, rootId = 0, onForbidden, confirmedMessages = emptyMessages, cacheScope}) {
    const scope = cacheScope ?? chatCacheScope(currentUser?.id);
    const conversationId = conversation?.id || 0;
    const identity = `${scope}:${conversationId}:${rootId}`;
    const [state, setState] = useState({identity, messages: [], warning: ''});
    const owner = useMemo(() => ({identity, mounted: true}), [identity]);
    const current = useRef(owner);
    const callbacks = useRef({conversation, onForbidden});
    current.current = owner;
    useEffect(() => { owner.mounted = true; return () => { owner.mounted = false; }; }, [owner]);
    callbacks.current = {conversation, onForbidden};
    const inFlight = useRef(new Map());
    const touched = useRef(new Set());
    const patch = useCallback(update => {
        if (current.current !== owner || !owner.mounted) return;
        setState(previous => update(previous.identity === identity ? previous : {identity, messages: [], warning: ''}));
    }, [identity, owner]);

    useEffect(() => {
        let active = true;
        const touchedBeforeRead = new Set(touched.current);
        void (async () => {
            try { await migrateLegacyOutbox(scope, conversationId); }
            catch { if (active) patch(previous => ({...previous, warning: storageWarning})); }
            const restored = await readOutboxMessages(scope, conversationId, rootId);
            if (!active) return;
            patch(previous => ({...previous, messages: [...restored.filter(message =>
                !previous.messages.some(existing => existing.client_message_id === message.client_message_id)
                && (!touched.current.has(`${identity}:${message.client_message_id}`) || touchedBeforeRead.has(`${identity}:${message.client_message_id}`))), ...previous.messages]}));
        })().catch(() => { if (active) patch(previous => ({...previous, warning: storageWarning})); });
        return () => { active = false; };
    }, [scope, conversationId, rootId, identity, patch]);

    const send = useCallback(async ({body, attachments, quotedMessage, clientId, originalBody}, request) => {
        const blocked = postingBlockedMessage(callbacks.current.conversation);
        if (blocked) throw new Error(blocked);
        const operation = `${identity}:${clientId}`;
        const existing = inFlight.current.get(operation);
        if (existing) return existing;
        const attempt = (async () => {
            touched.current.add(operation);
            const message = {id: `pending-${clientId}`, conversation_id: conversationId,
                thread_root_message_id: rootId || null, sender: currentUser, message_type: 'USER',
                body, original_body: originalBody, attachments, quoted_message: quotedMessage,
                client_message_id: clientId, created_at: new Date().toISOString(), reactions: [], reply_count: 0, pending: true};
            patch(previous => ({...previous, messages: [...previous.messages.filter(item => item.id !== message.id), message]}));
            const save = async value => {
                try { await saveOutboxMessage(scope, value); }
                catch { patch(previous => ({...previous, warning: storageWarning})); }
            };
            try {
                await save(message);
                let result;
                try {
                    if (current.current !== owner || !owner.mounted) throw new DOMException('Chat session changed', 'AbortError');
                    result = await request();
                }
                catch (sendError) {
                    const failed = {...message, pending: false, send_error: true, sendError};
                    patch(previous => ({...previous, messages: previous.messages.map(item => item.id === message.id ? failed : item)}));
                    await save(failed);
                    if (sendError?.status === 403 && current.current === owner && owner.mounted) {
                        void Promise.resolve(callbacks.current.onForbidden?.()).catch(() => undefined);
                    }
                    throw sendError;
                }
                // Persistence/refresh failure after a successful send is never a send failure.
                patch(previous => ({...previous, messages: previous.messages.filter(item => item.id !== message.id)}));
                try { await removeOutboxMessage(scope, conversationId, clientId, rootId); }
                catch { patch(previous => ({...previous, warning: 'Message sent, but its local retry copy could not be removed. Retrying uses the same message identifier.'})); }
                return current.current === owner && owner.mounted ? result : null;
            } finally { inFlight.current.delete(operation); }
        })();
        inFlight.current.set(operation, attempt);
        return attempt;
    }, [scope, conversationId, rootId, currentUser, identity, owner, patch]);

    const confirmedIds = useMemo(() => new Set(confirmedMessages.map(message => message.client_message_id).filter(Boolean)), [confirmedMessages]);
    useEffect(() => {
        if (state.identity !== identity) return;
        const accepted = state.messages.filter(message => confirmedIds.has(message.client_message_id));
        if (!accepted.length) return;
        accepted.forEach(message => touched.current.add(`${identity}:${message.client_message_id}`));
        patch(previous => ({...previous, messages: previous.messages.filter(message => !confirmedIds.has(message.client_message_id))}));
        void Promise.all(accepted.map(message => removeOutboxMessage(scope, conversationId, message.client_message_id, rootId)))
            .catch(() => patch(previous => ({...previous, warning: storageWarning})));
    }, [confirmedIds, state, identity, scope, conversationId, rootId, patch]);
    const visibleMessages = useMemo(() => state.identity === identity
        ? state.messages.filter(message => !confirmedIds.has(message.client_message_id)) : emptyMessages, [state, identity, confirmedIds]);
    return {messages: visibleMessages, warning: state.identity === identity ? state.warning : '', send};
}
